import { z } from 'zod';
import { Prisma } from '../prisma-types';
import Decimal from 'decimal.js';

/////////////////////////////////////////
// HELPER FUNCTIONS
/////////////////////////////////////////

// JSON
//------------------------------------------------------

export type NullableJsonInput = Prisma.JsonValue | null | 'JsonNull' | 'DbNull' | Prisma.NullTypes.DbNull | Prisma.NullTypes.JsonNull;

export const transformJsonNull = (v?: NullableJsonInput) => {
  if (!v || v === 'DbNull') return Prisma.NullTypes.DbNull;
  if (v === 'JsonNull') return Prisma.NullTypes.JsonNull;
  return v;
};

export const JsonValueSchema: z.ZodType<Prisma.JsonValue> = z.lazy(() =>
  z.union([
    z.string(),
    z.number(),
    z.boolean(),
    z.literal(null),
    z.record(z.string(), z.lazy(() => JsonValueSchema.optional())),
    z.array(z.lazy(() => JsonValueSchema)),
  ])
);

export type JsonValueType = z.infer<typeof JsonValueSchema>;

export const NullableJsonValue = z
  .union([JsonValueSchema, z.literal('DbNull'), z.literal('JsonNull')])
  .nullable()
  .transform((v) => transformJsonNull(v));

export type NullableJsonValueType = z.infer<typeof NullableJsonValue>;

export const InputJsonValueSchema: z.ZodType<Prisma.InputJsonValue> = z.lazy(() =>
  z.union([
    z.string(),
    z.number(),
    z.boolean(),
    z.object({ toJSON: z.any() }),
    z.record(z.string(), z.lazy(() => z.union([InputJsonValueSchema, z.literal(null)]))),
    z.array(z.lazy(() => z.union([InputJsonValueSchema, z.literal(null)]))),
  ])
);

export type InputJsonValueType = z.infer<typeof InputJsonValueSchema>;

// DECIMAL
//------------------------------------------------------

export const DecimalJsLikeSchema: z.ZodType<Prisma.DecimalJsLike> = z.object({
  d: z.array(z.number()),
  e: z.number(),
  s: z.number(),
  toFixed: z.any(),
})

export const DECIMAL_STRING_REGEX = /^(?:-?Infinity|NaN|-?(?:0[bB][01]+(?:\.[01]+)?(?:[pP][-+]?\d+)?|0[oO][0-7]+(?:\.[0-7]+)?(?:[pP][-+]?\d+)?|0[xX][\da-fA-F]+(?:\.[\da-fA-F]+)?(?:[pP][-+]?\d+)?|(?:\d+|\d*\.\d+)(?:[eE][-+]?\d+)?))$/;

export const isValidDecimalInput =
  (v?: null | string | number | Prisma.DecimalJsLike): v is string | number | Prisma.DecimalJsLike => {
    if (v === undefined || v === null) return false;
    return (
      (typeof v === 'object' && 'd' in v && 'e' in v && 's' in v && 'toFixed' in v) ||
      (typeof v === 'string' && DECIMAL_STRING_REGEX.test(v)) ||
      typeof v === 'number'
    )
  };

/////////////////////////////////////////
// ENUMS
/////////////////////////////////////////

export const TransactionIsolationLevelSchema = z.enum(['ReadUncommitted','ReadCommitted','RepeatableRead','Serializable']);

export const UserScalarFieldEnumSchema = z.enum(['id','name','email','emailVerified','image','createdAt','updatedAt','full_name','phoneNumber','phoneNumberVerified','birth_date','gender','city','ward']);

export const AccountScalarFieldEnumSchema = z.enum(['id','accountId','providerId','userId','accessToken','refreshToken','idToken','accessTokenExpiresAt','refreshTokenExpiresAt','scope','password','createdAt','updatedAt']);

export const SessionScalarFieldEnumSchema = z.enum(['id','expiresAt','token','createdAt','updatedAt','ipAddress','userAgent','userId']);

export const VerificationScalarFieldEnumSchema = z.enum(['id','identifier','value','expiresAt','createdAt','updatedAt']);

export const User_devicesScalarFieldEnumSchema = z.enum(['id','user_id','token','platform','created_at','updated_at']);

export const System_rolesScalarFieldEnumSchema = z.enum(['id','code','name','description','created_at']);

export const User_system_rolesScalarFieldEnumSchema = z.enum(['id','user_id','system_role_id','created_at']);

export const PlayersScalarFieldEnumSchema = z.enum(['id','user_id','nickname','bio','avatar_url','highest_elo','total_matches','total_wins','highest_streak','championships_won','gender','player_code','is_verified','verification_documents','docs','created_at','updated_at']);

export const OrganizationsScalarFieldEnumSchema = z.enum(['id','name','slug','description','is_public','verified_status','logo_url','banner_url','created_at','created_by','updated_at','updated_by']);

export const Organization_membersScalarFieldEnumSchema = z.enum(['id','organization_id','user_id','member_type','status','invited_by','joined_at','created_at','updated_at']);

export const OrganizersScalarFieldEnumSchema = z.enum(['id','user_id','type','name','display_name','license_type','description','verification_note','contact_email','contact_phone','address','contact_person','contact_position','logo_url','slug','fanpage_url','website_url','account_tier','verified_status','verified_by_user_id','verified_at','banner_url','docs','created_at','created_by','updated_at','updated_by']);

export const Billiards_typeScalarFieldEnumSchema = z.enum(['id','code','name','description','order_index','is_active','created_at','updated_at']);

export const Billiards_subtypeScalarFieldEnumSchema = z.enum(['id','billiards_type_id','code','name','description','default_scoring_style','default_target_score','default_max_turns','order_index','is_active','created_at','updated_at']);

export const Competition_formatScalarFieldEnumSchema = z.enum(['id','code','name','description','order_index','is_active','created_at','updated_at']);

export const CompositionScalarFieldEnumSchema = z.enum(['id','code','name','description','order_index','is_active','created_at','updated_at']);

export const TournamentsScalarFieldEnumSchema = z.enum(['id','organization_id','organizer_id','name','slug','description','cover_image_url','location_name','location_address','start_date','end_date','timezone','status','visibility','registration_open_at','registration_close_at','max_players','allow_waitlist','ruleset_note','contact_name','contact_phone','contact_email','organizer_note','logo_url','banner_url','docs','created_at','created_by','updated_at','updated_by']);

export const Tournament_competitionsScalarFieldEnumSchema = z.enum(['id','tournament_id','billiards_subtype_id','format_id','composition_id','name','description','player_limit','registration_fee','currency','status','third_place_match','apply_ranking','use_mebi_ranking','ranking_scope_id','account_number','account_name','bank_name','qr_code_url','regulation_file_url','registration_start_date','registration_end_date','event_start_date','event_end_date','allowed_ranks','province','ward','detail_address','banner_url','docs','created_at','created_by','updated_at','updated_by']);

export const Competition_phasesScalarFieldEnumSchema = z.enum(['id','competition_id','name','order_index','requires_draw','output_players_count','status','created_at','created_by','updated_at','updated_by']);

export const Competition_groupsScalarFieldEnumSchema = z.enum(['id','phase_id','parent_group_id','name','bracket_type','player_limit','qualifiers_count','created_at','created_by','updated_at','updated_by']);

export const Competition_stagesScalarFieldEnumSchema = z.enum(['id','competition_id','group_id','name','order_index','format','best_of','target_score','max_turns','is_final_stage','status','created_at','created_by','updated_at','updated_by']);

export const Competition_participantsScalarFieldEnumSchema = z.enum(['id','competition_id','player_id','seed_number','status','payment_status','payment_proof_url','payment_provider','payment_reference','payment_idempotency_key','payment_verified_at','payment_proof_submitted_at','payment_resend_requested_at','note_from_player','note_from_organizer','approved_at','approved_by','joined_at','is_active','total_wins','total_losses','created_at','created_by','updated_at','updated_by']);

export const Competition_phase_draw_assignmentsScalarFieldEnumSchema = z.enum(['id','phase_id','competition_participant_id','draw_number','canonical_seed','draw_batch','created_at','created_by','updated_at','updated_by']);

export const Competition_prizesScalarFieldEnumSchema = z.enum(['id','competition_id','rank_name','rank_position','quantity','cash_prize','physical_prize','created_at','created_by','updated_at','updated_by']);

export const Competition_prize_assignmentsScalarFieldEnumSchema = z.enum(['id','competition_id','competition_participant_id','competition_prize_id','prize_label','created_at','created_by','updated_at','updated_by']);

export const MatchesScalarFieldEnumSchema = z.enum(['id','stage_id','round','player1_id','player2_id','score1','score2','match_time','status','winner_id','bracket_position','table_number','referee_user_id','is_bye','player1_checkin_status','player2_checkin_status','created_at','created_by','updated_at','updated_by']);

export const Ranking_pluginScalarFieldEnumSchema = z.enum(['id','guid','plugin_id','capability_type','status','deleted_at','created_date','modified_date']);

export const Ranking_scopeScalarFieldEnumSchema = z.enum(['id','guid','scope_type','scope_object_id','status','deleted_at','created_date','modified_date']);

export const Ranking_configurationScalarFieldEnumSchema = z.enum(['id','guid','ranking_scope_id','ranking_plugin_id','capability_type','status','deleted_at','created_date','modified_date']);

export const Ranking_configuration_versionScalarFieldEnumSchema = z.enum(['id','guid','ranking_configuration_id','version','status','activated_at','created_date','modified_date']);

export const Ranking_configuration_valueScalarFieldEnumSchema = z.enum(['id','ranking_configuration_version_id','parameter_code','value','created_date','modified_date']);

export const Ranking_localized_fieldScalarFieldEnumSchema = z.enum(['id','ranking_plugin_id','target_type','target_value','locale','field','value','created_date','modified_date']);

export const Ranking_scoreScalarFieldEnumSchema = z.enum(['id','guid','player_id','billiards_type_id','ranking_scope_id','score','created_date','modified_date']);

export const NotificationsScalarFieldEnumSchema = z.enum(['id','user_id','type','title','content','avatar_url','sub_avatar_url','data','is_read','created_at','deleted_at']);

export const ReportsScalarFieldEnumSchema = z.enum(['id','user_id','title','description','report_type','status','request_payload','review_note','reviewed_at','reviewed_by','created_at','created_by','updated_at','updated_by']);

export const Ranking_settlementScalarFieldEnumSchema = z.enum(['id','guid','ranking_score_id','ranking_scope_id','ranking_configuration_id','ranking_configuration_version_id','score_before','score_delta','score_after','process_date','created_date','modified_date']);

export const Ranking_settlement_sourceScalarFieldEnumSchema = z.enum(['id','settlement_id','type','created_date']);

export const Ranking_competition_settlementScalarFieldEnumSchema = z.enum(['id','guid','settlement_source_id','competition_id','created_date','modified_date']);

export const Ranking_match_transactionScalarFieldEnumSchema = z.enum(['id','competition_settlement_id','match_id','opponent_player_id','sequence','score_before','score_delta','score_after','created_date','modified_date']);

export const Ranking_match_transaction_dataScalarFieldEnumSchema = z.enum(['id','ranking_match_transaction_id','key','value','value_type','created_date','modified_date']);

export const Ranking_adjustment_settlementScalarFieldEnumSchema = z.enum(['id','guid','settlement_source_id','adjustment_type','created_date','modified_date']);

export const Ranking_adjustment_transactionScalarFieldEnumSchema = z.enum(['id','adjustment_settlement_id','score_before','score_delta','score_after','created_date','modified_date']);

export const Ranking_adjustment_transaction_dataScalarFieldEnumSchema = z.enum(['id','ranking_adjustment_transaction_id','key','value','value_type','created_date','modified_date']);

export const Player_metricScalarFieldEnumSchema = z.enum(['id','player_id','key','type','string_value','integer_value','decimal_value','timestamp_value','datetime_value','evaluated_date','created_date','last_modified_date']);

export const Leaderboard_viewScalarFieldEnumSchema = z.enum(['ranking_score_id','player_id','billiards_type_id','ranking_scope_id','score','position','latest_competition_score_delta','latest_competition_changed_at']);

export const SortOrderSchema = z.enum(['asc','desc']);

export const NullableJsonNullValueInputSchema: z.ZodType<Prisma.NullableJsonNullValueInput> = z.enum(['DbNull','JsonNull',]).transform((value) => value === 'JsonNull' ? Prisma.JsonNull : value === 'DbNull' ? Prisma.DbNull : value);

export const QueryModeSchema = z.enum(['default','insensitive']);

export const NullsOrderSchema = z.enum(['first','last']);

export const JsonNullValueFilterSchema: z.ZodType<Prisma.JsonNullValueFilter> = z.enum(['DbNull','JsonNull','AnyNull',]).transform((value) => value === 'JsonNull' ? Prisma.JsonNull : value === 'DbNull' ? Prisma.DbNull : value === 'AnyNull' ? Prisma.AnyNull : value);

export const ranking_configuration_statusSchema = z.enum(['ENABLED','DISABLED']);

export type ranking_configuration_statusType = `${z.infer<typeof ranking_configuration_statusSchema>}`

export const ranking_configuration_version_statusSchema = z.enum(['DRAFT','ACTIVE','INACTIVE']);

export type ranking_configuration_version_statusType = `${z.infer<typeof ranking_configuration_version_statusSchema>}`

export const ranking_localization_target_typeSchema = z.enum(['PLUGIN','PARAMETER','OPTION','OPTION_VALUE','FORMULA','VARIABLE']);

export type ranking_localization_target_typeType = `${z.infer<typeof ranking_localization_target_typeSchema>}`

export const account_tierSchema = z.enum(['basic','premium','enterprise']);

export type account_tierType = `${z.infer<typeof account_tierSchema>}`

export const device_platformSchema = z.enum(['android','ios','web']);

export type device_platformType = `${z.infer<typeof device_platformSchema>}`

export const member_statusSchema = z.enum(['pending','active','inactive']);

export type member_statusType = `${z.infer<typeof member_statusSchema>}`

export const member_typeSchema = z.enum(['owner','manager','staff','member']);

export type member_typeType = `${z.infer<typeof member_typeSchema>}`

export const organizer_typeSchema = z.enum(['community','club','federation','business']);

export type organizer_typeType = `${z.infer<typeof organizer_typeSchema>}`

export const verification_statusSchema = z.enum(['pending','verified','rejected']);

export type verification_statusType = `${z.infer<typeof verification_statusSchema>}`

export const bracket_typeSchema = z.enum(['SINGLE_ELIMINATION','DOUBLE_ELIMINATION','ROUND_ROBIN']);

export type bracket_typeType = `${z.infer<typeof bracket_typeSchema>}`

export const checkin_status_enumSchema = z.enum(['NOT_CHECKED_IN','CHECKED_IN','LATE']);

export type checkin_status_enumType = `${z.infer<typeof checkin_status_enumSchema>}`

export const competition_statusSchema = z.enum(['draft','registration_open','registration_closed','ongoing','completed','cancelled']);

export type competition_statusType = `${z.infer<typeof competition_statusSchema>}`

export const general_status_enumSchema = z.enum(['draft','active','completed','cancelled']);

export type general_status_enumType = `${z.infer<typeof general_status_enumSchema>}`

export const match_statusSchema = z.enum(['UPCOMING','ONGOING','COMPLETED','CANCELLED']);

export type match_statusType = `${z.infer<typeof match_statusSchema>}`

export const payment_status_enumSchema = z.enum(['unpaid','paid','refunded']);

export type payment_status_enumType = `${z.infer<typeof payment_status_enumSchema>}`

export const registration_statusSchema = z.enum(['pending','approved','rejected','cancelled']);

export type registration_statusType = `${z.infer<typeof registration_statusSchema>}`

export const ranking_lifecycle_statusSchema = z.enum(['DRAFT','ENABLE','DISABLED']);

export type ranking_lifecycle_statusType = `${z.infer<typeof ranking_lifecycle_statusSchema>}`

export const ranking_capability_typeSchema = z.enum(['RATING','DECAY']);

export type ranking_capability_typeType = `${z.infer<typeof ranking_capability_typeSchema>}`

export const ranking_scope_typeSchema = z.enum(['SYSTEM','ORGANIZATION']);

export type ranking_scope_typeType = `${z.infer<typeof ranking_scope_typeSchema>}`

export const scoring_styleSchema = z.enum(['POINTS','FRAMES','TIME']);

export type scoring_styleType = `${z.infer<typeof scoring_styleSchema>}`

export const tournament_statusSchema = z.enum(['upcoming','ongoing','completed','cancelled']);

export type tournament_statusType = `${z.infer<typeof tournament_statusSchema>}`

export const visibility_enumSchema = z.enum(['public','private','unlisted']);

export type visibility_enumType = `${z.infer<typeof visibility_enumSchema>}`

export const report_statusSchema = z.enum(['pending','approved','rejected','cancelled']);

export type report_statusType = `${z.infer<typeof report_statusSchema>}`

export const report_typeSchema = z.enum(['general','organizer_change_request']);

export type report_typeType = `${z.infer<typeof report_typeSchema>}`

/////////////////////////////////////////
// MODELS
/////////////////////////////////////////

/////////////////////////////////////////
// USER SCHEMA
/////////////////////////////////////////

export const userSchema = z.object({
  id: z.string(),
  name: z.string(),
  email: z.string(),
  emailVerified: z.boolean(),
  image: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  full_name: z.string().nullable(),
  phoneNumber: z.string().nullable(),
  phoneNumberVerified: z.boolean().nullable(),
  birth_date: z.coerce.date().nullable(),
  gender: z.string().nullable(),
  city: z.string().nullable(),
  ward: z.string().nullable(),
})

export type user = z.infer<typeof userSchema>

/////////////////////////////////////////
// ACCOUNT SCHEMA
/////////////////////////////////////////

export const accountSchema = z.object({
  id: z.string(),
  accountId: z.string(),
  providerId: z.string(),
  userId: z.string(),
  accessToken: z.string().nullable(),
  refreshToken: z.string().nullable(),
  idToken: z.string().nullable(),
  accessTokenExpiresAt: z.coerce.date().nullable(),
  refreshTokenExpiresAt: z.coerce.date().nullable(),
  scope: z.string().nullable(),
  password: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
})

export type account = z.infer<typeof accountSchema>

/////////////////////////////////////////
// SESSION SCHEMA
/////////////////////////////////////////

export const sessionSchema = z.object({
  id: z.string(),
  expiresAt: z.coerce.date(),
  token: z.string(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  ipAddress: z.string().nullable(),
  userAgent: z.string().nullable(),
  userId: z.string(),
})

export type session = z.infer<typeof sessionSchema>

/////////////////////////////////////////
// VERIFICATION SCHEMA
/////////////////////////////////////////

export const verificationSchema = z.object({
  id: z.string(),
  identifier: z.string(),
  value: z.string(),
  expiresAt: z.coerce.date(),
  createdAt: z.coerce.date().nullable(),
  updatedAt: z.coerce.date().nullable(),
})

export type verification = z.infer<typeof verificationSchema>

/////////////////////////////////////////
// USER DEVICES SCHEMA
/////////////////////////////////////////

export const user_devicesSchema = z.object({
  platform: device_platformSchema,
  id: z.string(),
  user_id: z.string(),
  token: z.string(),
  created_at: z.coerce.date(),
  updated_at: z.coerce.date(),
})

export type user_devices = z.infer<typeof user_devicesSchema>

/////////////////////////////////////////
// SYSTEM ROLES SCHEMA
/////////////////////////////////////////

export const system_rolesSchema = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  created_at: z.coerce.date().nullable(),
})

export type system_roles = z.infer<typeof system_rolesSchema>

/////////////////////////////////////////
// USER SYSTEM ROLES SCHEMA
/////////////////////////////////////////

export const user_system_rolesSchema = z.object({
  id: z.string(),
  user_id: z.string(),
  system_role_id: z.string(),
  created_at: z.coerce.date().nullable(),
})

export type user_system_roles = z.infer<typeof user_system_rolesSchema>

/////////////////////////////////////////
// PLAYERS SCHEMA
/////////////////////////////////////////

export const playersSchema = z.object({
  id: z.string(),
  user_id: z.string(),
  nickname: z.string().nullable(),
  bio: z.string().nullable(),
  avatar_url: z.string().nullable(),
  highest_elo: z.number().int().nullable(),
  total_matches: z.number().int().nullable(),
  total_wins: z.number().int().nullable(),
  highest_streak: z.number().int().nullable(),
  championships_won: z.number().int().nullable(),
  gender: z.string().nullable(),
  player_code: z.string().nullable(),
  is_verified: z.boolean().nullable(),
  verification_documents: JsonValueSchema.nullable(),
  docs: JsonValueSchema.nullable(),
  created_at: z.coerce.date().nullable(),
  updated_at: z.coerce.date().nullable(),
})

export type players = z.infer<typeof playersSchema>

/////////////////////////////////////////
// ORGANIZATIONS SCHEMA
/////////////////////////////////////////

export const organizationsSchema = z.object({
  id: z.string(),
  name: z.string(),
  slug: z.string(),
  description: z.string().nullable(),
  is_public: z.boolean().nullable(),
  verified_status: z.string(),
  logo_url: z.string().nullable(),
  banner_url: z.string().nullable(),
  created_at: z.coerce.date().nullable(),
  created_by: z.string().nullable(),
  updated_at: z.coerce.date().nullable(),
  updated_by: z.string().nullable(),
})

export type organizations = z.infer<typeof organizationsSchema>

/////////////////////////////////////////
// ORGANIZATION MEMBERS SCHEMA
/////////////////////////////////////////

export const organization_membersSchema = z.object({
  member_type: member_typeSchema,
  status: member_statusSchema,
  id: z.string(),
  organization_id: z.string(),
  user_id: z.string(),
  invited_by: z.string().nullable(),
  joined_at: z.coerce.date().nullable(),
  created_at: z.coerce.date().nullable(),
  updated_at: z.coerce.date().nullable(),
})

export type organization_members = z.infer<typeof organization_membersSchema>

/////////////////////////////////////////
// ORGANIZERS SCHEMA
/////////////////////////////////////////

export const organizersSchema = z.object({
  type: organizer_typeSchema,
  account_tier: account_tierSchema,
  verified_status: verification_statusSchema,
  id: z.string(),
  user_id: z.string(),
  name: z.string(),
  display_name: z.string().nullable(),
  license_type: z.string().nullable(),
  description: z.string().nullable(),
  verification_note: z.string().nullable(),
  contact_email: z.string(),
  contact_phone: z.string(),
  address: z.string().nullable(),
  contact_person: z.string().nullable(),
  contact_position: z.string().nullable(),
  logo_url: z.string().nullable(),
  slug: z.string().nullable(),
  fanpage_url: z.string().nullable(),
  website_url: z.string().nullable(),
  verified_by_user_id: z.string().nullable(),
  verified_at: z.coerce.date().nullable(),
  banner_url: z.string().nullable(),
  docs: JsonValueSchema.nullable(),
  created_at: z.coerce.date().nullable(),
  created_by: z.string().nullable(),
  updated_at: z.coerce.date().nullable(),
  updated_by: z.string().nullable(),
})

export type organizers = z.infer<typeof organizersSchema>

/////////////////////////////////////////
// BILLIARDS TYPE SCHEMA
/////////////////////////////////////////

export const billiards_typeSchema = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  order_index: z.number().int().nullable(),
  is_active: z.boolean().nullable(),
  created_at: z.coerce.date().nullable(),
  updated_at: z.coerce.date().nullable(),
})

export type billiards_type = z.infer<typeof billiards_typeSchema>

/////////////////////////////////////////
// BILLIARDS SUBTYPE SCHEMA
/////////////////////////////////////////

export const billiards_subtypeSchema = z.object({
  default_scoring_style: scoring_styleSchema.nullable(),
  id: z.string(),
  billiards_type_id: z.string(),
  code: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  default_target_score: z.number().int().nullable(),
  default_max_turns: z.number().int().nullable(),
  order_index: z.number().int().nullable(),
  is_active: z.boolean().nullable(),
  created_at: z.coerce.date().nullable(),
  updated_at: z.coerce.date().nullable(),
})

export type billiards_subtype = z.infer<typeof billiards_subtypeSchema>

/////////////////////////////////////////
// COMPETITION FORMAT SCHEMA
/////////////////////////////////////////

export const competition_formatSchema = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  order_index: z.number().int().nullable(),
  is_active: z.boolean().nullable(),
  created_at: z.coerce.date().nullable(),
  updated_at: z.coerce.date().nullable(),
})

export type competition_format = z.infer<typeof competition_formatSchema>

/////////////////////////////////////////
// COMPOSITION SCHEMA
/////////////////////////////////////////

export const compositionSchema = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  order_index: z.number().int().nullable(),
  is_active: z.boolean().nullable(),
  created_at: z.coerce.date().nullable(),
  updated_at: z.coerce.date().nullable(),
})

export type composition = z.infer<typeof compositionSchema>

/////////////////////////////////////////
// TOURNAMENTS SCHEMA
/////////////////////////////////////////

export const tournamentsSchema = z.object({
  status: tournament_statusSchema,
  visibility: visibility_enumSchema.nullable(),
  id: z.string(),
  organization_id: z.string(),
  organizer_id: z.string().nullable(),
  name: z.string(),
  slug: z.string().nullable(),
  description: z.string().nullable(),
  cover_image_url: z.string().nullable(),
  location_name: z.string().nullable(),
  location_address: z.string().nullable(),
  start_date: z.coerce.date(),
  end_date: z.coerce.date(),
  timezone: z.string().nullable(),
  registration_open_at: z.coerce.date().nullable(),
  registration_close_at: z.coerce.date().nullable(),
  max_players: z.number().int().nullable(),
  allow_waitlist: z.boolean().nullable(),
  ruleset_note: z.string().nullable(),
  contact_name: z.string().nullable(),
  contact_phone: z.string().nullable(),
  contact_email: z.string().nullable(),
  organizer_note: z.string().nullable(),
  logo_url: z.string().nullable(),
  banner_url: z.string().nullable(),
  docs: JsonValueSchema.nullable(),
  created_at: z.coerce.date().nullable(),
  created_by: z.string().nullable(),
  updated_at: z.coerce.date().nullable(),
  updated_by: z.string().nullable(),
})

export type tournaments = z.infer<typeof tournamentsSchema>

/////////////////////////////////////////
// TOURNAMENT COMPETITIONS SCHEMA
/////////////////////////////////////////

export const tournament_competitionsSchema = z.object({
  status: competition_statusSchema.nullable(),
  id: z.string(),
  tournament_id: z.string(),
  billiards_subtype_id: z.string(),
  format_id: z.string().nullable(),
  composition_id: z.string().nullable(),
  name: z.string().nullable(),
  description: z.string().nullable(),
  player_limit: z.number().int().nullable(),
  registration_fee: z.instanceof(Prisma.Decimal, { message: "Field 'registration_fee' must be a Decimal. Location: ['Models', 'tournament_competitions']"}).nullable(),
  currency: z.string().nullable(),
  third_place_match: z.boolean().nullable(),
  apply_ranking: z.boolean().nullable(),
  use_mebi_ranking: z.boolean().nullable(),
  ranking_scope_id: z.bigint().nullable(),
  account_number: z.string().nullable(),
  account_name: z.string().nullable(),
  bank_name: z.string().nullable(),
  qr_code_url: z.string().nullable(),
  regulation_file_url: z.string().nullable(),
  registration_start_date: z.coerce.date().nullable(),
  registration_end_date: z.coerce.date().nullable(),
  event_start_date: z.coerce.date().nullable(),
  event_end_date: z.coerce.date().nullable(),
  allowed_ranks: z.string().array(),
  province: z.string().nullable(),
  ward: z.string().nullable(),
  detail_address: z.string().nullable(),
  banner_url: z.string().nullable(),
  docs: JsonValueSchema.nullable(),
  created_at: z.coerce.date().nullable(),
  created_by: z.string().nullable(),
  updated_at: z.coerce.date().nullable(),
  updated_by: z.string().nullable(),
})

export type tournament_competitions = z.infer<typeof tournament_competitionsSchema>

/////////////////////////////////////////
// COMPETITION PHASES SCHEMA
/////////////////////////////////////////

export const competition_phasesSchema = z.object({
  status: general_status_enumSchema,
  id: z.string(),
  competition_id: z.string(),
  name: z.string(),
  order_index: z.number().int(),
  requires_draw: z.boolean().nullable(),
  output_players_count: z.number().int().nullable(),
  created_at: z.coerce.date().nullable(),
  created_by: z.string().nullable(),
  updated_at: z.coerce.date().nullable(),
  updated_by: z.string().nullable(),
})

export type competition_phases = z.infer<typeof competition_phasesSchema>

/////////////////////////////////////////
// COMPETITION GROUPS SCHEMA
/////////////////////////////////////////

export const competition_groupsSchema = z.object({
  bracket_type: bracket_typeSchema,
  id: z.string(),
  phase_id: z.string(),
  parent_group_id: z.string().nullable(),
  name: z.string(),
  player_limit: z.number().int().nullable(),
  qualifiers_count: z.number().int().nullable(),
  created_at: z.coerce.date().nullable(),
  created_by: z.string().nullable(),
  updated_at: z.coerce.date().nullable(),
  updated_by: z.string().nullable(),
})

export type competition_groups = z.infer<typeof competition_groupsSchema>

/////////////////////////////////////////
// COMPETITION STAGES SCHEMA
/////////////////////////////////////////

export const competition_stagesSchema = z.object({
  status: general_status_enumSchema,
  id: z.string(),
  competition_id: z.string(),
  group_id: z.string().nullable(),
  name: z.string(),
  order_index: z.number().int(),
  format: z.string(),
  best_of: z.number().int().nullable(),
  target_score: z.number().int().nullable(),
  max_turns: z.number().int().nullable(),
  is_final_stage: z.boolean().nullable(),
  created_at: z.coerce.date().nullable(),
  created_by: z.string().nullable(),
  updated_at: z.coerce.date().nullable(),
  updated_by: z.string().nullable(),
})

export type competition_stages = z.infer<typeof competition_stagesSchema>

/////////////////////////////////////////
// COMPETITION PARTICIPANTS SCHEMA
/////////////////////////////////////////

export const competition_participantsSchema = z.object({
  status: registration_statusSchema,
  payment_status: payment_status_enumSchema,
  id: z.string(),
  competition_id: z.string(),
  player_id: z.string(),
  seed_number: z.number().int().nullable(),
  payment_proof_url: z.string().nullable(),
  payment_provider: z.string().nullable(),
  payment_reference: z.string().nullable(),
  payment_idempotency_key: z.string().nullable(),
  payment_verified_at: z.coerce.date().nullable(),
  payment_proof_submitted_at: z.coerce.date().nullable(),
  payment_resend_requested_at: z.coerce.date().nullable(),
  note_from_player: z.string().nullable(),
  note_from_organizer: z.string().nullable(),
  approved_at: z.coerce.date().nullable(),
  approved_by: z.string().nullable(),
  joined_at: z.coerce.date(),
  is_active: z.boolean(),
  total_wins: z.number().int(),
  total_losses: z.number().int(),
  created_at: z.coerce.date(),
  created_by: z.string().nullable(),
  updated_at: z.coerce.date(),
  updated_by: z.string().nullable(),
})

export type competition_participants = z.infer<typeof competition_participantsSchema>

/////////////////////////////////////////
// COMPETITION PHASE DRAW ASSIGNMENTS SCHEMA
/////////////////////////////////////////

export const competition_phase_draw_assignmentsSchema = z.object({
  id: z.string(),
  phase_id: z.string(),
  competition_participant_id: z.string(),
  draw_number: z.number().int(),
  canonical_seed: z.number().int().nullable(),
  draw_batch: z.number().int(),
  created_at: z.coerce.date().nullable(),
  created_by: z.string().nullable(),
  updated_at: z.coerce.date().nullable(),
  updated_by: z.string().nullable(),
})

export type competition_phase_draw_assignments = z.infer<typeof competition_phase_draw_assignmentsSchema>

/////////////////////////////////////////
// COMPETITION PRIZES SCHEMA
/////////////////////////////////////////

export const competition_prizesSchema = z.object({
  id: z.string(),
  competition_id: z.string(),
  rank_name: z.string(),
  rank_position: z.number().int(),
  quantity: z.number().int(),
  cash_prize: z.instanceof(Prisma.Decimal, { message: "Field 'cash_prize' must be a Decimal. Location: ['Models', 'competition_prizes']"}).nullable(),
  physical_prize: z.string().nullable(),
  created_at: z.coerce.date().nullable(),
  created_by: z.string().nullable(),
  updated_at: z.coerce.date().nullable(),
  updated_by: z.string().nullable(),
})

export type competition_prizes = z.infer<typeof competition_prizesSchema>

/////////////////////////////////////////
// COMPETITION PRIZE ASSIGNMENTS SCHEMA
/////////////////////////////////////////

export const competition_prize_assignmentsSchema = z.object({
  id: z.string(),
  competition_id: z.string(),
  competition_participant_id: z.string(),
  competition_prize_id: z.string().nullable(),
  prize_label: z.string(),
  created_at: z.coerce.date().nullable(),
  created_by: z.string().nullable(),
  updated_at: z.coerce.date().nullable(),
  updated_by: z.string().nullable(),
})

export type competition_prize_assignments = z.infer<typeof competition_prize_assignmentsSchema>

/////////////////////////////////////////
// MATCHES SCHEMA
/////////////////////////////////////////

export const matchesSchema = z.object({
  status: match_statusSchema,
  player1_checkin_status: checkin_status_enumSchema,
  player2_checkin_status: checkin_status_enumSchema,
  id: z.string(),
  stage_id: z.string().nullable(),
  round: z.number().int().nullable(),
  player1_id: z.string().nullable(),
  player2_id: z.string().nullable(),
  score1: z.number().int().nullable(),
  score2: z.number().int().nullable(),
  match_time: z.coerce.date().nullable(),
  winner_id: z.string().nullable(),
  bracket_position: z.number().int().nullable(),
  table_number: z.string().nullable(),
  referee_user_id: z.string().nullable(),
  is_bye: z.boolean(),
  created_at: z.coerce.date().nullable(),
  created_by: z.string().nullable(),
  updated_at: z.coerce.date().nullable(),
  updated_by: z.string().nullable(),
})

export type matches = z.infer<typeof matchesSchema>

/////////////////////////////////////////
// RANKING PLUGIN SCHEMA
/////////////////////////////////////////

export const ranking_pluginSchema = z.object({
  capability_type: ranking_capability_typeSchema,
  status: ranking_lifecycle_statusSchema,
  id: z.bigint(),
  guid: z.string(),
  plugin_id: z.string(),
  deleted_at: z.coerce.date().nullable(),
  created_date: z.coerce.date(),
  modified_date: z.coerce.date(),
})

export type ranking_plugin = z.infer<typeof ranking_pluginSchema>

/////////////////////////////////////////
// RANKING SCOPE SCHEMA
/////////////////////////////////////////

export const ranking_scopeSchema = z.object({
  scope_type: ranking_scope_typeSchema,
  status: ranking_lifecycle_statusSchema,
  id: z.bigint(),
  guid: z.string(),
  scope_object_id: z.string().nullable(),
  deleted_at: z.coerce.date().nullable(),
  created_date: z.coerce.date(),
  modified_date: z.coerce.date(),
})

export type ranking_scope = z.infer<typeof ranking_scopeSchema>

/////////////////////////////////////////
// RANKING CONFIGURATION SCHEMA
/////////////////////////////////////////

export const ranking_configurationSchema = z.object({
  capability_type: ranking_capability_typeSchema,
  status: ranking_configuration_statusSchema,
  id: z.bigint(),
  guid: z.string(),
  ranking_scope_id: z.bigint(),
  ranking_plugin_id: z.bigint(),
  deleted_at: z.coerce.date().nullable(),
  created_date: z.coerce.date(),
  modified_date: z.coerce.date(),
})

export type ranking_configuration = z.infer<typeof ranking_configurationSchema>

/////////////////////////////////////////
// RANKING CONFIGURATION VERSION SCHEMA
/////////////////////////////////////////

export const ranking_configuration_versionSchema = z.object({
  status: ranking_configuration_version_statusSchema,
  id: z.bigint(),
  guid: z.string(),
  ranking_configuration_id: z.bigint(),
  version: z.number().int(),
  activated_at: z.coerce.date().nullable(),
  created_date: z.coerce.date(),
  modified_date: z.coerce.date(),
})

export type ranking_configuration_version = z.infer<typeof ranking_configuration_versionSchema>

/////////////////////////////////////////
// RANKING CONFIGURATION VALUE SCHEMA
/////////////////////////////////////////

export const ranking_configuration_valueSchema = z.object({
  id: z.bigint(),
  ranking_configuration_version_id: z.bigint(),
  parameter_code: z.string(),
  value: z.string(),
  created_date: z.coerce.date(),
  modified_date: z.coerce.date(),
})

export type ranking_configuration_value = z.infer<typeof ranking_configuration_valueSchema>

/////////////////////////////////////////
// RANKING LOCALIZED FIELD SCHEMA
/////////////////////////////////////////

export const ranking_localized_fieldSchema = z.object({
  target_type: ranking_localization_target_typeSchema,
  id: z.bigint(),
  ranking_plugin_id: z.bigint(),
  target_value: z.string().nullable(),
  locale: z.string(),
  field: z.string(),
  value: z.string(),
  created_date: z.coerce.date(),
  modified_date: z.coerce.date(),
})

export type ranking_localized_field = z.infer<typeof ranking_localized_fieldSchema>

/////////////////////////////////////////
// RANKING SCORE SCHEMA
/////////////////////////////////////////

export const ranking_scoreSchema = z.object({
  id: z.bigint(),
  guid: z.string(),
  player_id: z.string(),
  billiards_type_id: z.string(),
  ranking_scope_id: z.bigint(),
  score: z.instanceof(Prisma.Decimal, { message: "Field 'score' must be a Decimal. Location: ['Models', 'ranking_score']"}),
  created_date: z.coerce.date(),
  modified_date: z.coerce.date(),
})

export type ranking_score = z.infer<typeof ranking_scoreSchema>

/////////////////////////////////////////
// NOTIFICATIONS SCHEMA
/////////////////////////////////////////

export const notificationsSchema = z.object({
  id: z.string(),
  user_id: z.string(),
  type: z.string().nullable(),
  title: z.string(),
  content: z.string().nullable(),
  avatar_url: z.string().nullable(),
  sub_avatar_url: z.string().nullable(),
  data: JsonValueSchema.nullable(),
  is_read: z.boolean().nullable(),
  created_at: z.coerce.date().nullable(),
  deleted_at: z.coerce.date().nullable(),
})

export type notifications = z.infer<typeof notificationsSchema>

/////////////////////////////////////////
// REPORTS SCHEMA
/////////////////////////////////////////

export const reportsSchema = z.object({
  report_type: report_typeSchema,
  status: report_statusSchema,
  id: z.string(),
  user_id: z.string(),
  title: z.string(),
  description: z.string(),
  request_payload: JsonValueSchema.nullable(),
  review_note: z.string().nullable(),
  reviewed_at: z.coerce.date().nullable(),
  reviewed_by: z.string().nullable(),
  created_at: z.coerce.date(),
  created_by: z.string().nullable(),
  updated_at: z.coerce.date(),
  updated_by: z.string().nullable(),
})

export type reports = z.infer<typeof reportsSchema>

/////////////////////////////////////////
// RANKING SETTLEMENT SCHEMA
/////////////////////////////////////////

export const ranking_settlementSchema = z.object({
  id: z.bigint(),
  guid: z.string(),
  ranking_score_id: z.bigint(),
  ranking_scope_id: z.bigint(),
  ranking_configuration_id: z.bigint().nullable(),
  ranking_configuration_version_id: z.bigint().nullable(),
  score_before: z.instanceof(Prisma.Decimal, { message: "Field 'score_before' must be a Decimal. Location: ['Models', 'ranking_settlement']"}),
  score_delta: z.instanceof(Prisma.Decimal, { message: "Field 'score_delta' must be a Decimal. Location: ['Models', 'ranking_settlement']"}),
  score_after: z.instanceof(Prisma.Decimal, { message: "Field 'score_after' must be a Decimal. Location: ['Models', 'ranking_settlement']"}),
  process_date: z.coerce.date(),
  created_date: z.coerce.date(),
  modified_date: z.coerce.date(),
})

export type ranking_settlement = z.infer<typeof ranking_settlementSchema>

/////////////////////////////////////////
// RANKING SETTLEMENT SOURCE SCHEMA
/////////////////////////////////////////

export const ranking_settlement_sourceSchema = z.object({
  id: z.bigint(),
  settlement_id: z.bigint(),
  type: z.string(),
  created_date: z.coerce.date(),
})

export type ranking_settlement_source = z.infer<typeof ranking_settlement_sourceSchema>

/////////////////////////////////////////
// RANKING COMPETITION SETTLEMENT SCHEMA
/////////////////////////////////////////

export const ranking_competition_settlementSchema = z.object({
  id: z.bigint(),
  guid: z.string(),
  settlement_source_id: z.bigint(),
  competition_id: z.string(),
  created_date: z.coerce.date(),
  modified_date: z.coerce.date(),
})

export type ranking_competition_settlement = z.infer<typeof ranking_competition_settlementSchema>

/////////////////////////////////////////
// RANKING MATCH TRANSACTION SCHEMA
/////////////////////////////////////////

export const ranking_match_transactionSchema = z.object({
  id: z.bigint(),
  competition_settlement_id: z.bigint(),
  match_id: z.string(),
  opponent_player_id: z.string(),
  sequence: z.number().int(),
  score_before: z.instanceof(Prisma.Decimal, { message: "Field 'score_before' must be a Decimal. Location: ['Models', 'ranking_match_transaction']"}),
  score_delta: z.instanceof(Prisma.Decimal, { message: "Field 'score_delta' must be a Decimal. Location: ['Models', 'ranking_match_transaction']"}),
  score_after: z.instanceof(Prisma.Decimal, { message: "Field 'score_after' must be a Decimal. Location: ['Models', 'ranking_match_transaction']"}),
  created_date: z.coerce.date(),
  modified_date: z.coerce.date(),
})

export type ranking_match_transaction = z.infer<typeof ranking_match_transactionSchema>

/////////////////////////////////////////
// RANKING MATCH TRANSACTION DATA SCHEMA
/////////////////////////////////////////

export const ranking_match_transaction_dataSchema = z.object({
  id: z.bigint(),
  ranking_match_transaction_id: z.bigint(),
  key: z.string(),
  value: z.string(),
  value_type: z.string(),
  created_date: z.coerce.date(),
  modified_date: z.coerce.date(),
})

export type ranking_match_transaction_data = z.infer<typeof ranking_match_transaction_dataSchema>

/////////////////////////////////////////
// RANKING ADJUSTMENT SETTLEMENT SCHEMA
/////////////////////////////////////////

export const ranking_adjustment_settlementSchema = z.object({
  id: z.bigint(),
  guid: z.string(),
  settlement_source_id: z.bigint(),
  adjustment_type: z.string(),
  created_date: z.coerce.date(),
  modified_date: z.coerce.date(),
})

export type ranking_adjustment_settlement = z.infer<typeof ranking_adjustment_settlementSchema>

/////////////////////////////////////////
// RANKING ADJUSTMENT TRANSACTION SCHEMA
/////////////////////////////////////////

export const ranking_adjustment_transactionSchema = z.object({
  id: z.bigint(),
  adjustment_settlement_id: z.bigint(),
  score_before: z.instanceof(Prisma.Decimal, { message: "Field 'score_before' must be a Decimal. Location: ['Models', 'ranking_adjustment_transaction']"}),
  score_delta: z.instanceof(Prisma.Decimal, { message: "Field 'score_delta' must be a Decimal. Location: ['Models', 'ranking_adjustment_transaction']"}),
  score_after: z.instanceof(Prisma.Decimal, { message: "Field 'score_after' must be a Decimal. Location: ['Models', 'ranking_adjustment_transaction']"}),
  created_date: z.coerce.date(),
  modified_date: z.coerce.date(),
})

export type ranking_adjustment_transaction = z.infer<typeof ranking_adjustment_transactionSchema>

/////////////////////////////////////////
// RANKING ADJUSTMENT TRANSACTION DATA SCHEMA
/////////////////////////////////////////

export const ranking_adjustment_transaction_dataSchema = z.object({
  id: z.bigint(),
  ranking_adjustment_transaction_id: z.bigint(),
  key: z.string(),
  value: z.string(),
  value_type: z.string(),
  created_date: z.coerce.date(),
  modified_date: z.coerce.date(),
})

export type ranking_adjustment_transaction_data = z.infer<typeof ranking_adjustment_transaction_dataSchema>

/////////////////////////////////////////
// PLAYER METRIC SCHEMA
/////////////////////////////////////////

export const player_metricSchema = z.object({
  id: z.bigint(),
  player_id: z.string(),
  key: z.string(),
  type: z.string(),
  string_value: z.string().nullable(),
  integer_value: z.number().int().nullable(),
  decimal_value: z.instanceof(Prisma.Decimal, { message: "Field 'decimal_value' must be a Decimal. Location: ['Models', 'player_metric']"}).nullable(),
  timestamp_value: z.coerce.date().nullable(),
  datetime_value: z.coerce.date().nullable(),
  evaluated_date: z.coerce.date(),
  created_date: z.coerce.date(),
  last_modified_date: z.coerce.date(),
})

export type player_metric = z.infer<typeof player_metricSchema>

/////////////////////////////////////////
// LEADERBOARD VIEW SCHEMA
/////////////////////////////////////////

export const leaderboard_viewSchema = z.object({
  ranking_score_id: z.bigint(),
  player_id: z.string(),
  billiards_type_id: z.string(),
  ranking_scope_id: z.bigint(),
  score: z.instanceof(Prisma.Decimal, { message: "Field 'score' must be a Decimal. Location: ['Models', 'leaderboard_view']"}),
  position: z.bigint(),
  latest_competition_score_delta: z.instanceof(Prisma.Decimal, { message: "Field 'latest_competition_score_delta' must be a Decimal. Location: ['Models', 'leaderboard_view']"}).nullable(),
  latest_competition_changed_at: z.coerce.date().nullable(),
})

export type leaderboard_view = z.infer<typeof leaderboard_viewSchema>
